import type { ActivityEventRow, JobRow } from "@/features/app/types";
import { isActiveJobStatus } from "@/features/app/utils";

type ActivityMetadata = Record<string, unknown>;

export type CombinedActivityEvent = {
  id: string;
  kind: ActivityEventRow["kind"];
  actor: ActivityEventRow["actor"];
  summary: string;
  metadata: ActivityMetadata;
  createdAt: string;
  events: ActivityEventRow[];
};

const DEDUPED_CTA_ACTIONS = new Set<string>(["generate_plan", "run_proposal"]);
const TRANSCRIPT_INITIATION_SUMMARY_BY_JOB_KIND: Partial<Record<JobRow["kind"], string>> = {
  "import+feasibility": "Start import and feasibility",
  questions: "Start steering question generation",
  plan: "Start outline generation",
  "proposal+pdf": "Start proposal generation"
};
const RECENT_USER_EVENT_WINDOW_MS = 20_000;
const SYNTHETIC_EVENT_OFFSET_MS = 1;
const DISPLAY_START_EVENT_OFFSET_MS = 1;

const parseActivityMetadata = (value: string): ActivityMetadata => {
  try {
    const parsed = JSON.parse(value) as ActivityMetadata;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const titleCase = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

const getFileLabel = (filePath: unknown): string | null => {
  if (typeof filePath !== "string" || filePath.trim().length === 0) {
    return null;
  }

  const normalizedPath = filePath.replaceAll("\\", "/");
  return normalizedPath.split("/").filter(Boolean).at(-1) ?? normalizedPath;
};

const getAggregationKey = (event: ActivityEventRow, metadata: ActivityMetadata): string | null => {
  if (event.kind === "user_change" && event.summary === "Steering answer updated" && metadata.field === "answer") {
    return "user_change:steering_answer_updated";
  }

  if (event.kind === "library_change") {
    const scope = typeof metadata.scope === "string" ? metadata.scope : null;
    const action = typeof metadata.action === "string" ? metadata.action : null;
    if (scope && action) {
      return `library_change:${scope}:${action}`;
    }
  }

  return null;
};

const buildCombinedSummary = (
  events: ActivityEventRow[],
  aggregationKey: string,
  lastMetadata: ActivityMetadata
): { summary: string; metadata: ActivityMetadata } => {
  if (events.length === 1) {
    return {
      summary: events[0]?.summary ?? "",
      metadata: lastMetadata
    };
  }

  if (aggregationKey === "user_change:steering_answer_updated") {
    const questionIds = events
      .map((event) => parseActivityMetadata(event.metadata_json).questionId)
      .filter((value): value is string => typeof value === "string");

    return {
      summary: `${events.length} steering answers updated`,
      metadata: {
        ...lastMetadata,
        combinedCount: events.length,
        questionIds
      }
    };
  }

  if (aggregationKey.startsWith("library_change:")) {
    const [, scope, action] = aggregationKey.split(":");
    const fileLabels = events
      .map((event) => getFileLabel(parseActivityMetadata(event.metadata_json).filePath))
      .filter((value): value is string => Boolean(value));
    const scopePluralLabel = scope === "cvs" ? "team profiles" : "company library files";
    const prefix = events.length === 1 ? titleCase(scopePluralLabel) : `${events.length} ${scopePluralLabel}`;
    const suffix = fileLabels.length > 0 ? `: ${fileLabels.join(", ")}` : "";

    return {
      summary: `${prefix} ${action}${suffix}`,
      metadata: {
        ...lastMetadata,
        combinedCount: events.length,
        filePaths: fileLabels
      }
    };
  }

  return {
    summary: events.at(-1)?.summary ?? "",
    metadata: lastMetadata
  };
};

export const combineActivityEvents = (activityEvents: ActivityEventRow[]): CombinedActivityEvent[] => {
  const combined: CombinedActivityEvent[] = [];

  for (const event of activityEvents) {
    const metadata = parseActivityMetadata(event.metadata_json);
    const aggregationKey = getAggregationKey(event, metadata);
    const previous = combined.at(-1);
    const previousAggregationKey =
      previous ? getAggregationKey(previous.events.at(-1) ?? previous.events[0], previous.metadata) : null;

    if (
      aggregationKey &&
      previous &&
      previous.kind === event.kind &&
      previous.actor === event.actor &&
      previousAggregationKey === aggregationKey
    ) {
      const nextEvents = [...previous.events, event];
      const summaryState = buildCombinedSummary(nextEvents, aggregationKey, metadata);
      previous.id = event.id;
      previous.summary = summaryState.summary;
      previous.metadata = summaryState.metadata;
      previous.createdAt = event.created_at;
      previous.events = nextEvents;
      continue;
    }

    const summaryState = aggregationKey
      ? buildCombinedSummary([event], aggregationKey, metadata)
      : { summary: event.summary, metadata };

    combined.push({
      id: event.id,
      kind: event.kind,
      actor: event.actor,
      summary: summaryState.summary,
      metadata: summaryState.metadata,
      createdAt: event.created_at,
      events: [event]
    });
  }

  return combined;
};

const hasRecentUserEvent = (activityEvents: ActivityEventRow[], timestamp: string): boolean => {
  const targetTime = new Date(timestamp).getTime();

  for (const event of activityEvents) {
    if (event.actor !== "user") {
      continue;
    }

    const eventTime = new Date(event.created_at).getTime();
    if (eventTime > targetTime) {
      continue;
    }

    if (targetTime - eventTime <= RECENT_USER_EVENT_WINDOW_MS) {
      return true;
    }
  }

  return false;
};

export const buildActivityTranscriptEvents = (
  activityEvents: ActivityEventRow[],
  caseJobs: JobRow[]
): CombinedActivityEvent[] => {
  const combinedEvents = combineActivityEvents(activityEvents);
  const syntheticEvents = caseJobs
    .filter((job) => TRANSCRIPT_INITIATION_SUMMARY_BY_JOB_KIND[job.kind] !== undefined)
    .filter((job) => !(job.kind === "proposal+pdf" && hasRecentUserEvent(activityEvents, job.created_at)))
    .map((job) => {
      const createdAt = new Date(new Date(job.created_at).getTime() - SYNTHETIC_EVENT_OFFSET_MS).toISOString();
      const summary = TRANSCRIPT_INITIATION_SUMMARY_BY_JOB_KIND[job.kind] ?? job.kind;

      return {
        id: `synthetic-initiation:${job.id}`,
        kind: "user_change" as const,
        actor: "user" as const,
        summary,
        metadata: {
          synthetic: true,
          sourceJobId: job.id,
          sourceJobKind: job.kind
        },
        createdAt,
        events: []
      };
    });

  return [...combinedEvents, ...syntheticEvents].sort(
    (left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime()
  );
};

export const findLatestDedupedCtaEventIds = (events: CombinedActivityEvent[]): Set<string> => {
  const latestEventIdsByAction = new Map<string, string>();

  for (const event of events) {
    const cta = event.metadata.cta as { action?: string } | null | undefined;
    if (!cta?.action || !DEDUPED_CTA_ACTIONS.has(cta.action)) {
      continue;
    }

    latestEventIdsByAction.set(cta.action, event.id);
  }

  return new Set(latestEventIdsByAction.values());
};

export const getActivityEventDisplayTime = (
  event: CombinedActivityEvent,
  relatedJob: JobRow | null
): number => {
  const eventTime = new Date(event.createdAt).getTime();
  if (!relatedJob || event.kind !== "workflow_status" || event.summary !== "Proposal generation started") {
    return eventTime;
  }

  const jobTime = new Date(relatedJob.created_at).getTime();
  return Math.min(eventTime, jobTime - DISPLAY_START_EVENT_OFFSET_MS);
};

export const getStandaloneActivityJobs = (caseJobs: JobRow[], inlineJobIds: Set<string>): JobRow[] =>
  [...caseJobs]
    .sort((left, right) => new Date(left.created_at).getTime() - new Date(right.created_at).getTime())
    .filter((job) => !inlineJobIds.has(job.id))
    .filter((job) => isActiveJobStatus(job.status) || job.kind === "import+feasibility");

export const getActivityMetadata = parseActivityMetadata;
